import { useState } from 'react';
import { X, FileText, Target, TrendingUp, Brain, Sparkles, BarChart3, ExternalLink, ArrowRight, Layers, Shield, Award, BookOpen, Workflow, Clock, CheckCircle, Users, Database, Rocket, PhoneCall, ClipboardCheck, PackageCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import MethodologyFlowchart from './MethodologyFlowchart';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface FormationGestionDocumentaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: string;
  domain: string;
  initialTab?: string;
}

const baseTabs = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: Target },
  { id: 'esprit', label: 'Esprit & Philosophie', icon: Brain },
  { id: 'contenu', label: 'Contenu & Valeur', icon: FileText },
  { id: 'psychologie', label: 'Psychologie & Impact', icon: Sparkles },
  { id: 'processus', label: 'Processus Complet', icon: BarChart3 },
];

const bzTelecomTrainingTracks = [
  {
    title: 'Téléphonie hébergée',
    purpose: 'Vendre une solution de communication fiable, évolutive et simple à gérer pour remplacer ou moderniser la téléphonie du client.',
    process: [
      'Qualifier le contexte: nombre d\'utilisateurs, sites, numéros, postes, réception, files d\'appel, horaires et contraintes actuelles.',
      'Identifier les douleurs: coûts, pannes, appels manqués, transfert d\'appels, télétravail, mobilité, rapports et administration.',
      'Valider l\'environnement réseau: lien Internet, pare-feu, commutateurs, VLAN voix, PoE, qualité de service et redondance.',
      'Présenter l\'architecture cible: utilisateurs, extensions, groupes d\'appel, menus vocaux, messagerie vocale, enregistrements et options mobiles.',
      'Préparer la proposition: forfaits, équipements, portabilité, installation, formation, support et échéancier de transition.',
      'Planifier la mise en service: collecte des informations, test des appels, portabilité des numéros, formation et suivi après activation.',
    ],
    products: [
      'Lignes et utilisateurs de téléphonie hébergée',
      'Téléphones IP, bases sans-fil DECT et accessoires',
      'Menus vocaux, files d\'appel, groupes d\'appel et règles horaires',
      'Messagerie vocale vers courriel et options de mobilité',
      'Portabilité des numéros et configuration des appels entrants/sortants',
      'Services d\'installation, formation et support continu',
    ],
  },
  {
    title: 'Câblage structuré et fibre optique',
    purpose: 'Vendre une infrastructure physique propre, certifiable et prête pour la croissance réseau, Wi-Fi, téléphonie, caméras et équipements critiques.',
    process: [
      'Faire la prise de besoin: plans, nombre de prises, types d\'équipements, zones de travail, salle serveur et contraintes de bâtiment.',
      'Valider le site: parcours de câbles, plafonds, conduits, distances, obstacles, normes, heures de travail et accès aux locaux.',
      'Définir la solution: cuivre Cat6/Cat6A, fibre monomode ou multimode, panneaux, cabinets, rack, gestion de câbles et identification.',
      'Évaluer les risques: distance maximale, environnement électrique, travaux en hauteur, perçage, interruption client et besoin de sous-traitance.',
      'Produire la soumission: matériel, main-d\'œuvre, certification, documentation, photos, plans de repérage et exclusions clairement nommées.',
      'Livrer et fermer: installation, tests, étiquetage, certification, remise de documentation et validation finale avec le client.',
    ],
    products: [
      'Câblage structuré cuivre Cat6 et Cat6A',
      'Fibre optique monomode ou multimode',
      'Panneaux de brassage, prises murales, keystones et cordons',
      'Racks, cabinets, gestionnaires de câbles et accessoires',
      'Tests, certification, étiquetage et documentation',
      'Installation, déplacement, ajout et nettoyage de câblage existant',
    ],
  },
];

const bzTelecomServiceProducts = [
  {
    title: 'Solution clé en main',
    items: ['Analyse des besoins', 'Configuration complète du système', 'Portabilité des numéros existants', 'Installation des téléphones IP', 'Formation des utilisateurs', 'Maintenance et mises à jour', 'Support technique local', 'Surveillance proactive de la plateforme'],
  },
  {
    title: 'Fonctions à forte valeur',
    items: ['Standard téléphonique automatisé (IVR)', 'Files d\'attente intelligentes', 'Groupes de sonnerie', 'Boîtes vocales personnalisées', 'Transfert d\'appels', 'Renvoi intelligent', 'Applications mobiles', 'Softphone pour ordinateur', 'Messagerie vocale par courriel', 'Transcription des messages vocaux', 'Rapports détaillés', 'Enregistrement des appels'],
  },
  {
    title: 'Ajouts BZ Telecom',
    items: ['Plateforme SMS centralisée', 'Rapports de files d\'attente', 'EVA', 'Calendrier d\'urgence', 'Transcription de messages vocaux'],
  },
];

const telephonyGlossary = [
  { term: 'Téléphonie hébergée', plain: 'Le système téléphonique n\'est plus une grosse boîte chez le client: il est hébergé dans le nuage et géré à distance.' },
  { term: 'VoIP', plain: 'La voix passe par Internet ou par le réseau informatique au lieu d\'une ligne téléphonique traditionnelle.' },
  { term: 'PBX', plain: 'Le cerveau du système téléphonique: il gère les extensions, les transferts, les menus et les règles d\'appels.' },
  { term: 'Extension', plain: 'Le numéro interne d\'un utilisateur ou d\'un poste, par exemple 201 pour rejoindre la réception.' },
  { term: 'DID / numéro direct', plain: 'Un numéro public qui sonne directement à une personne, une équipe ou un service sans passer par la réception.' },
  { term: 'Portabilité', plain: 'Le transfert des numéros actuels du client vers la nouvelle solution, sans changer ses numéros connus.' },
  { term: 'Menu vocal / IVR', plain: 'Le message automatisé du type: appuyez sur 1 pour les ventes, 2 pour le support, 3 pour l\'administration.' },
  { term: 'File d\'appel', plain: 'Une ligne d\'attente intelligente qui distribue les appels à plusieurs employés selon des règles définies.' },
  { term: 'Softphone', plain: 'Une application sur ordinateur ou cellulaire qui permet de faire et recevoir des appels comme avec un téléphone de bureau.' },
  { term: 'PoE', plain: 'Le courant électrique passe dans le câble réseau; pratique pour alimenter un téléphone IP sans bloc d\'alimentation.' },
  { term: 'QoS', plain: 'Une priorité donnée à la voix sur le réseau pour éviter les coupures, délais ou appels de mauvaise qualité.' },
  { term: 'SIP', plain: 'Le protocole utilisé par plusieurs systèmes VoIP pour établir et gérer les appels.' },
];

const hostedTelephonyUseCases = [
  'Éliminer les coûts liés à un serveur téléphonique local',
  'Réduire les frais de maintenance',
  'Permettre le télétravail',
  'Offrir des fonctionnalités avancées sans investissement important',
  'Simplifier l\'ajout de nouveaux utilisateurs',
  'Centraliser toutes les communications de l\'entreprise',
];

const hostedTelephonyAdvantages = [
  { title: 'Flexibilité', text: 'Les employés peuvent utiliser un téléphone IP, un ordinateur, un téléphone intelligent ou une tablette tout en conservant leur numéro professionnel.' },
  { title: 'Évolutivité', text: 'L\'ajout d\'un nouvel employé prend généralement quelques minutes, sans acheter une nouvelle centrale téléphonique.' },
  { title: 'Haute disponibilité', text: 'Les plateformes sont généralement hébergées dans plusieurs centres de données pour rediriger les appels si un serveur devient indisponible.' },
  { title: 'Coûts prévisibles', text: 'La majorité des fournisseurs fonctionnent avec un abonnement mensuel par utilisateur, ce qui facilite la planification budgétaire.' },
];

const bzTelecomAiFeatures = [
  'Transcription automatique des appels',
  'Transcription des messages vocaux',
  'Résumés intelligents des conversations',
  'Évaluation de la qualité des appels',
  'Analyse des performances des agents',
  'Recherche par mots-clés dans les appels enregistrés',
  'Automatisation de certaines tâches administratives',
];

const bzTelecomIdealFor = [
  'Entreprises possédant plusieurs succursales',
  'Équipes en télétravail',
  'Centres de contact',
  'Entreprises de services',
  'Commerces',
  'Organismes publics',
  'Entreprises en croissance',
];

const bzTelecomInfrastructure = [
  'Infrastructure redondante',
  'Sauvegardes régulières',
  'Communications sécurisées',
  'Surveillance proactive des services',
  'Mises à jour effectuées par nos équipes',
];

const bzTelecomReasons = [
  'Un accompagnement local qui comprend la réalité des PME',
  'Une solution clé en main, de l\'analyse au support',
  'Une expertise télécom combinée à l\'expertise TI de BZ',
  'Une plateforme évolutive qui accompagne la croissance',
  'Des outils modernes pour améliorer l\'expérience client et la mobilité des employés',
];

const bzTelecomWhyParagraphs = [
  'En choisissant la téléphonie hébergée de BZ Telecom, vous profitez d\'un partenaire québécois qui vous accompagne à chaque étape de votre projet. Notre approche va bien au-delà de la simple fourniture d\'un service téléphonique: nous analysons vos besoins, configurons une solution adaptée à votre entreprise, assurons la migration de vos numéros, formons vos utilisateurs et demeurons disponibles pour vous accompagner au quotidien.',
  'Grâce à notre expertise en téléphonie IP, en centres de contact et en intelligence artificielle, nous offrons une plateforme évolutive qui améliore les communications, augmente la productivité des équipes et contribue à offrir une expérience client de qualité. Avec un service local, une maintenance incluse, des technologies de pointe et des fonctionnalités avancées, la téléphonie hébergée de BZ Telecom constitue une solution complète, fiable et prête à accompagner la croissance de votre entreprise.',
];

const bzTelecomRelatedProducts = [
  {
    title: 'Plateforme SMS centralisée',
    description: 'La plateforme SMS centralisée de BZ Telecom permet aux entreprises de gérer l\'ensemble de leurs communications par messages texte à partir d\'une interface unique. Plusieurs employés peuvent utiliser un même numéro d\'entreprise tout en conservant l\'historique complet des conversations.',
    note: 'Cette solution facilite les échanges avec les clients, améliore le suivi des communications et assure une réponse rapide, peu importe l\'employé qui prend en charge la conversation.',
    features: ['Boîte de réception partagée pour toute l\'équipe', 'Historique complet des conversations', 'Réponses simultanées par plusieurs agents', 'Messages automatisés et modèles de réponses', 'Notifications en temps réel', 'Accès depuis un ordinateur ou un appareil mobile', 'Conservation de l\'historique des échanges', 'Intégration avec les systèmes téléphoniques et CRM, selon les besoins'],
  },
  {
    title: 'Système évolué de rapports de files d\'attente',
    description: 'Le système évolué de rapports de files d\'attente de BZ Telecom fournit une vision complète de la performance de votre centre d\'appels. Grâce à des tableaux de bord détaillés et à des rapports personnalisés, les gestionnaires suivent les indicateurs clés afin d\'améliorer le service à la clientèle et la productivité des équipes.',
    note: 'Ces informations permettent d\'identifier rapidement les périodes de forte affluence, d\'ajuster les horaires et d\'optimiser les ressources.',
    features: ['Nombre d\'appels reçus et répondus', 'Appels abandonnés', 'Temps moyen d\'attente', 'Temps moyen de conversation', 'Temps de traitement après appel', 'Niveau de service (SLA)', 'Performance individuelle des agents', 'Performance par équipe', 'Occupation des agents', 'Rapports en temps réel et historiques', 'Exportation Excel, PDF et CSV'],
  },
  {
    title: 'EVA - Plateforme intelligente d\'enregistrement des appels',
    description: 'EVA est la solution avancée de BZ Telecom dédiée à l\'enregistrement, à l\'analyse et à l\'amélioration de la qualité des communications téléphoniques. Elle combine un enregistrement sécurisé à long terme avec des outils d\'intelligence artificielle pour transformer chaque appel en information exploitable.',
    note: 'Contrairement à un simple système d\'enregistrement, EVA aide les gestionnaires à évaluer la qualité du service, à former les équipes et à identifier les occasions d\'amélioration.',
    features: ['Enregistrement longue durée selon les politiques internes ou obligations réglementaires', 'Recherche rapide par numéro, agent, client, date ou durée de l\'appel', 'Transcription automatique des conversations', 'Résumé intelligent avec motif, points abordés, décisions, actions et suivis', 'Évaluation automatisée des agents', 'Recherche intelligente par mot-clé, dossier, client ou sujet', 'Tableaux de bord sur la qualité, les tendances et les performances'],
    details: [{ title: 'Critères d\'évaluation possibles', items: ['Qualité de l\'accueil', 'Respect du script de l\'entreprise', 'Professionnalisme', 'Courtoisie et empathie', 'Résolution du problème', 'Respect des procédures internes', 'Conformité réglementaire'] }],
  },
  {
    title: 'Service de calendrier d\'urgence',
    description: 'Le service de calendrier d\'urgence de BZ Telecom permet de gérer automatiquement les horaires d\'urgence, les gardes et les périodes de disponibilité du personnel. En cas d\'appel reçu en dehors des heures normales ou lors d\'une situation particulière, le système dirige automatiquement les appels vers les personnes responsables selon un calendrier prédéfini.',
    note: 'Cette solution réduit les interventions manuelles, assure une prise en charge rapide des appels critiques et garantit que le bon intervenant est joint au bon moment.',
    features: ['Gestion des horaires de garde', 'Acheminement intelligent des appels selon la date et l\'heure', 'Escalade automatique vers un second intervenant si le premier ne répond pas', 'Notification par appel, SMS ou courriel', 'Modification des horaires en temps réel', 'Consultation des calendriers à distance', 'Historique des interventions et des changements de garde'],
    audience: 'Particulièrement adapté aux entreprises offrant un service d\'urgence ou un soutien 24/7: services techniques, maintenance, transport, santé ou tout secteur nécessitant une disponibilité continue.',
  },
  {
    title: 'Transcription des messages vocaux',
    description: 'La transcription des messages vocaux convertit automatiquement un message laissé dans une boîte vocale en texte grâce à l\'intelligence artificielle. L\'utilisateur reçoit une version écrite dans son courriel, son application mobile ou son portail de téléphonie.',
    note: 'Cette fonctionnalité permet de traiter les demandes plus rapidement, retrouver un message en quelques secondes et offrir un meilleur service à la clientèle, tout en conservant l\'enregistrement audio original.',
    features: ['Lecture rapide des messages sans les écouter', 'Consultation discrète en réunion ou dans un lieu public', 'Recherche de mots-clés dans les messages', 'Archivage simplifié des communications', 'Gain de temps pour les utilisateurs', 'Meilleur suivi des demandes clients'],
    details: [
      { title: 'Comment cela fonctionne?', items: ['Un client laisse un message vocal', 'Le système enregistre le message', 'L\'intelligence artificielle analyse l\'enregistrement', 'Le message est converti en texte', 'L\'utilisateur reçoit le fichier audio, la transcription et, selon la configuration, un résumé'] },
      { title: 'Exemple de résultat', items: ['Message: Bonjour, ici Jean Tremblay de Construction ABC. Je vous appelle concernant notre rendez-vous de demain matin. Merci de me rappeler au 418-555-1234.', 'Résumé: Client Jean Tremblay, Construction ABC. Sujet: confirmation d\'un rendez-vous. Action recommandée: rappeler le client au 418-555-1234.'] },
    ],
    audience: 'Particulièrement utile pour les équipes de vente, le soutien technique, les services d\'urgence et toute entreprise recevant un volume important d\'appels.',
  },
];

export default function FormationGestionDocumentaireModal({
  isOpen,
  onClose,
  department,
  domain,
  initialTab = 'overview',
}: FormationGestionDocumentaireModalProps) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['formation_gestion_documentaire_last_ai_analysis'] ?? null) : null;
  const navigate = useNavigate();
  const isBZTelecom = department.toLowerCase().includes('bz telecom');
  const tabs = isBZTelecom
    ? [...baseTabs, { id: 'bz-telecom', label: 'BZ Telecom', icon: PhoneCall }]
    : baseTabs;

  if (!isOpen) return null;

  const handleGoToGestionDocumentaire = () => {
    navigate(`/gestion-documentaire?domain=${encodeURIComponent(domain)}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] my-4 max-h-[95vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-indigo-700 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              <div className="flex items-start gap-4 pr-12">
                <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="text-sm font-semibold text-white/80">Formation Holistique</div>
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded-full">
                      <Sparkles className="w-3 h-3 text-yellow-300" />
                      <span className="text-xs font-semibold text-white">IA</span>
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold mb-2">Gestion Documentaire PRIsME</h2>
                  <p className="text-white/90 text-lg mb-2">{department}</p>
                  {lastAnalysisDate && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-white/70">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-bz-blue border-b-2 border-bz-blue bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'overview' && <OverviewTab />}
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'contenu' && <ContenuTab />}
                  {activeTab === 'psychologie' && <PsychologieTab />}
                  {activeTab === 'processus' && <ProcessusTab />}
                  {activeTab === 'bz-telecom' && <BZTelecomTrainingTab />}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer avec CTA */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-8 py-6 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Prêt à créer de la valeur ajoutée pour vos clients ?
              </div>
              <button
                onClick={handleGoToGestionDocumentaire}
                className="flex items-center gap-2 px-6 py-3 bg-bz-blue text-white rounded-lg font-semibold hover:bg-bz-dark transition-colors shadow-lg hover:shadow-xl"
              >
                <span>Accéder à la Gestion Documentaire</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Vue d'ensemble
function OverviewTab() {
  return (
    <div className="space-y-6">
      {/* Section Principale - La Richesse PRIsME */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-bz-blue">
        <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Rocket className="w-6 h-6 text-bz-blue" />
          La Richesse PRIsME - 80% des Livrables Client
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          PRIsME génère <strong>300-400 livrables tangibles</strong> sous forme de documents normés. 
          <strong className="text-bz-blue"> 80% des livrables client se font par gestion documentaire</strong>, 
          créant une richesse incroyable non seulement par son <strong>contenu</strong>, mais aussi par son 
          <strong>contenant</strong> et le <strong>livrable client</strong>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-bz-blue">
            <div className="text-3xl font-bold text-bz-blue mb-1">300-400</div>
            <div className="text-sm text-gray-600">Livrables tangibles</div>
            <div className="text-xs text-gray-500 mt-1">80% des livrables client</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-emerald-600">
            <div className="text-3xl font-bold text-emerald-600 mb-1">5</div>
            <div className="text-sm text-gray-600">Niveaux de qualité</div>
            <div className="text-xs text-gray-500 mt-1">Niveau 3 minimum</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-purple-600">
            <div className="text-3xl font-bold text-purple-600 mb-1">100%</div>
            <div className="text-sm text-gray-600">Normés Standards</div>
            <div className="text-xs text-gray-500 mt-1">ITIL/COBIT/NIST/ISO</div>
          </div>
        </div>
      </div>

      {/* Section Valeur Ajoutée BZ */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-300">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="w-6 h-6 text-emerald-600" />
          Valeur Ajoutée BZ & Client
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border-2 border-emerald-200">
            <h5 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Pour BZ
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• <strong>Efficacité maximale</strong>: Génération rapide vs création manuelle</li>
              <li>• <strong>Scalabilité</strong>: Traiter plusieurs clients simultanément</li>
              <li>• <strong>Qualité garantie</strong>: Standards internationaux intégrés</li>
              <li>• <strong>Différenciation</strong>: Positionnement leader en excellence TI</li>
              <li>• <strong>Actif stratégique</strong>: Bibliothèque documentaire qui s'enrichit</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
            <h5 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Pour le Client
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• <strong>Documents professionnels</strong>: Qualité CA/DG</li>
              <li>• <strong>Conformité garantie</strong>: Alignés sur les normes</li>
              <li>• <strong>Livraison rapide</strong>: Minutes vs heures/jours</li>
              <li>• <strong>Valeur perçue</strong>: Renforce la confiance</li>
              <li>• <strong>Actionnable</strong>: Documents directement utilisables</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Graphique de répartition des documents */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-bz-blue" />
          Répartition des Documents par Domaine
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { domain: 'TI', count: 45, color: 'bg-blue-500', percentage: 25, type: 'client' },
            { domain: 'Cybersécurité', count: 38, color: 'bg-red-500', percentage: 21, type: 'client' },
            { domain: 'Ventes', count: 33, color: 'bg-emerald-500', percentage: 18, type: 'client' },
            { domain: 'Administration', count: 22, color: 'bg-purple-500', percentage: 12, type: 'client/interne' },
            { domain: 'Employé', count: 15, color: 'bg-amber-500', percentage: 8, type: 'interne' },
            { domain: 'Projet', count: 24, color: 'bg-indigo-500', percentage: 13, type: 'client' },
          ].map((item) => (
            <div key={item.domain} className="text-center">
              <div className={`${item.color} text-white p-4 rounded-lg mb-2`}>
                <div className="text-2xl font-bold">{item.count}</div>
                <div className="text-xs opacity-90">{item.domain}</div>
                <div className="text-[10px] opacity-75 mt-1">({item.type})</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className={`${item.color} h-2 rounded-full transition-all`}
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
              <div className="text-xs text-gray-600 mt-1">{item.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-bz-blue" />
            Objectif
          </h4>
          <p className="text-gray-700 leading-relaxed">
            Créer de la <strong>valeur ajoutée</strong> pour les clients grâce au <strong>volume</strong>, 
            à la <strong>qualité</strong> et à l'<strong>agilité</strong> de nos livrables documentaires.
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Impact
          </h4>
          <p className="text-gray-700 leading-relaxed">
            Chaque document normé renforce la <strong>crédibilité</strong>, la <strong>conformité</strong> 
            et la <strong>professionnalisation</strong> de nos services clients.
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-amber-900 mb-2">💡 Point Clé</h4>
        <p className="text-amber-800 leading-relaxed">
          C'est dans cette section que nous créons la valeur ajoutée aux clients. 
          Le <strong>volume</strong> de documents, leur <strong>qualité</strong> normée et l'<strong>agilité</strong> 
          de génération font toute la différence.
        </p>
      </div>
    </div>
  );
}

// Esprit & Philosophie
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          L'Esprit PRIsME - Synergie IA + Humain
        </h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          La gestion documentaire PRIsME n'est pas qu'un simple processus technique. 
          C'est une <strong>philosophie</strong> qui allie <strong>intelligence artificielle</strong> 
          et <strong>expertise humaine</strong> pour créer des documents de valeur exceptionnelle.
          <strong className="text-purple-700"> 80% des livrables client</strong> passent par ce processus, 
          faisant de la gestion documentaire le <strong>cœur de la valeur ajoutée BZ</strong>.
        </p>
      </div>

      {/* Standards Internationaux Intégrés */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-blue-600" />
          Standards Internationaux Intégrés
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Chaque document généré par PRIsME est aligné sur les meilleurs standards internationaux, 
          garantissant <strong>conformité</strong>, <strong>crédibilité</strong> et <strong>professionnalisme</strong>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
            <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              ITIL 4 (Service Management)
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Service Level Management</li>
              <li>• Incident Management (P1-P4)</li>
              <li>• Change Management</li>
              <li>• Problem Management</li>
              <li>• Service Catalog Management</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
            <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5" />
              COBIT 2019 (Gouvernance)
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• APO01 - Gérer le programme TI</li>
              <li>• APO08 - Gérer les relations</li>
              <li>• DSS02 - Gérer les demandes</li>
              <li>• EDM01 - Assurer la gouvernance</li>
              <li>• APO10 - Gérer les fournisseurs</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-red-200">
            <h5 className="font-bold text-red-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              NIST CSF 2.0 (Cybersécurité)
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• RS - Respond (Réponse incidents)</li>
              <li>• RC - Recover (Récupération)</li>
              <li>• PR - Protect (Protection)</li>
              <li>• DE - Detect (Détection)</li>
              <li>• GV - Govern (Gouvernance)</li>
            </ul>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-green-200">
            <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              ISO 27001 (SMSI)
            </h5>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• A.15 - Relations fournisseurs</li>
              <li>• A.16 - Gestion incidents</li>
              <li>• A.17 - Continuité d'activité</li>
              <li>• A.18 - Conformité</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Graphique de synergie */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          La Synergie IA + Humain
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">IA seule</span>
                <span className="text-sm font-bold text-gray-900">65%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6">
                <div className="bg-blue-500 h-6 rounded-full" style={{ width: '65%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Humain seul</span>
                <span className="text-sm font-bold text-gray-900">70%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6">
                <div className="bg-purple-500 h-6 rounded-full" style={{ width: '70%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">IA + Humain (PRIsME)</span>
                <span className="text-sm font-bold text-emerald-600">95%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6">
                <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 h-6 rounded-full" style={{ width: '95%' }} />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-purple-200">
            <h5 className="font-bold text-gray-900 mb-3 text-lg">💡 La Synergie</h5>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              <strong>IA + Humain = Excellence</strong>. L'IA génère rapidement et efficacement, 
              l'humain perfectionne et personnalise.
            </p>
            <div className="bg-white/80 p-3 rounded-lg">
              <div className="text-2xl font-bold text-emerald-600 mb-1">+30%</div>
              <div className="text-xs text-gray-600">Amélioration vs approche unique</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">🤖 Intelligence Artificielle</h4>
          <p className="text-gray-700 text-sm leading-relaxed mb-3">
            L'IA génère, standardise et structure selon les meilleurs standards internationaux 
            (ITIL, COBIT, NIST, ISO). Elle assure la <strong>cohérence</strong> et la <strong>normalisation</strong>.
          </p>
          <div className="flex gap-2 mt-4">
            {['ITIL', 'COBIT', 'NIST', 'ISO'].map((std) => (
              <span key={std} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                {std}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">🧠 Expertise Humaine</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            L'expert humain apporte la <strong>valeur ajoutée</strong>, le <strong>jugement</strong> 
            et la <strong>personnalisation</strong> qui transforment un document généré en livrable de qualité.
          </p>
          <div className="mt-4 flex items-center gap-2 text-sm text-purple-600">
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold">Valeur ajoutée BZ</span>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500">
        <h4 className="text-xl font-bold text-blue-900 mb-3">La Synergie</h4>
        <p className="text-blue-800 leading-relaxed">
          <strong>IA + Humain = Excellence</strong>. L'IA génère rapidement et efficacement, 
          l'humain perfectionne et personnalise. Cette combinaison crée des documents 
          qui surpassent ce que l'un ou l'autre pourrait produire seul.
        </p>
      </div>
    </div>
  );
}

// Contenu & Valeur
function ContenuTab() {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">La Triple Valeur</h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          Chaque document PRIsME crée de la valeur à trois niveaux :
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-emerald-200">
            <div className="text-2xl mb-2">📄</div>
            <h4 className="font-bold text-gray-900 mb-2">Le Contenu</h4>
            <p className="text-sm text-gray-700">
              Information structurée, normée et alignée sur les meilleures pratiques. 
              <strong>Valeur intellectuelle</strong> et <strong>connaissance</strong>.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-blue-200">
            <div className="text-2xl mb-2">📋</div>
            <h4 className="font-bold text-gray-900 mb-2">Le Contenant</h4>
            <p className="text-sm text-gray-700">
              Format professionnel, présentation soignée, structure claire. 
              <strong>Valeur esthétique</strong> et <strong>crédibilité</strong>.
            </p>
          </div>

          <div className="bg-white p-5 rounded-lg shadow-sm border-2 border-purple-200">
            <div className="text-2xl mb-2">🎁</div>
            <h4 className="font-bold text-gray-900 mb-2">Le Livrable Client</h4>
            <p className="text-sm text-gray-700">
              Document final livré, utilisable, actionnable. 
              <strong>Valeur opérationnelle</strong> et <strong>impact business</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Graphique de croissance */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-emerald-600" />
          Croissance de la Bibliothèque Documentaire
        </h4>
        <div className="space-y-3">
          {[
            { month: 'Q1 2026', count: 50, color: 'bg-blue-500' },
            { month: 'Q2 2026', count: 120, color: 'bg-indigo-500' },
            { month: 'Q3 2026', count: 200, color: 'bg-purple-500' },
            { month: 'Q4 2026', count: 280, color: 'bg-pink-500' },
            { month: 'Q1 2027', count: 350, color: 'bg-emerald-500' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="w-24 text-sm font-medium text-gray-700">{item.month}</div>
              <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                <div 
                  className={`${item.color} h-8 rounded-full flex items-center justify-end pr-3 text-white text-sm font-semibold transition-all`}
                  style={{ width: `${(item.count / 400) * 100}%` }}
                >
                  {item.count}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Graphique en barres - Distribution prévue par niveau pour 2026 */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="w-6 h-6 text-purple-600" />
          Distribution Prévue par Niveau de Qualité - 2026
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          Niveau minimum acceptable : <strong>Niveau 3</strong>. Les niveaux 1-3 seront livrés à 100%, 
          le niveau 4 à 65% et le niveau 5 à 40% pour l'année 2026.
        </p>
        <div className="grid grid-cols-5 gap-3">
          {[
            { level: 'Niveau 1', percentage: 100, color: 'bg-gray-400', label: 'Ad hoc' },
            { level: 'Niveau 2', percentage: 100, color: 'bg-blue-400', label: 'Initial' },
            { level: 'Niveau 3', percentage: 100, color: 'bg-indigo-400', label: 'Défini' },
            { level: 'Niveau 4', percentage: 65, color: 'bg-purple-400', label: 'Géré' },
            { level: 'Niveau 5', percentage: 40, color: 'bg-emerald-400', label: 'Optimisé' },
          ].map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="bg-gray-100 rounded-lg p-4 mb-2" style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div 
                  className={`${item.color} rounded-t-lg transition-all`}
                  style={{ height: `${item.percentage}%` }}
                />
              </div>
              <div className="text-sm font-semibold text-gray-700">{item.level}</div>
              <div className="text-xs text-gray-500">{item.label}</div>
              <div className="text-lg font-bold text-gray-900 mt-1">{item.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-3">📊 Volume = Richesse</h4>
        <p className="text-gray-700 leading-relaxed">
          Avec <strong>300-400 livrables</strong>, PRIsME crée une richesse documentaire inégalée. 
          Chaque document s'ajoute à la bibliothèque, créant une <strong>base de connaissances</strong> 
          qui s'enrichit continuellement et qui devient un <strong>actif stratégique</strong> pour BZ.
        </p>
      </div>
    </div>
  );
}

function BZTelecomTrainingTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-900 via-blue-800 to-emerald-700 text-white p-6 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-xl">
            <PhoneCall className="w-7 h-7" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white/80 mb-1">Formation BZ Telecom</div>
            <h3 className="text-2xl font-bold mb-3">Téléphonie hébergée, câblage structuré et fibre optique</h3>
            <p className="text-white/90 leading-relaxed">
              Référence interne pour standardiser la prise de besoin, la qualification, la proposition et la livraison
              des solutions de communication et d'infrastructure physique vendues par BZ Telecom.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {bzTelecomTrainingTracks.map((track) => (
          <section key={track.title} className="rounded-xl border border-blue-200 bg-blue-50/70 p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-blue-700 text-white p-3 rounded-xl">
                <ClipboardCheck className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900">{track.title}</h4>
                <p className="text-sm text-gray-700 leading-relaxed mt-1">{track.purpose}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-700" />
                  Processus de vente
                </h5>
                <ol className="space-y-2 text-sm text-gray-700">
                  {track.process.map((step, index) => (
                    <li key={step} className="flex gap-2">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-700 text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <PackageCheck className="w-5 h-5 text-emerald-700" />
                  Produits vendus
                </h5>
                <ul className="space-y-2 text-sm text-gray-700">
                  {track.products.map((product) => (
                    <li key={product} className="flex gap-2">
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                      <span>{product}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="rounded-xl border border-cyan-200 bg-cyan-50/70 p-5">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <PhoneCall className="w-6 h-6 text-cyan-700" />
          La téléphonie hébergée de BZ Telecom
        </h4>
        <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
          <p>
            La téléphonie hébergée de BZ Telecom est une solution complète de communication d'entreprise qui permet
            aux PME de bénéficier d'un système moderne, performant et évolutif, sans installer ni maintenir une
            centrale téléphonique sur leurs propres serveurs.
          </p>
          <p>
            La plateforme est hébergée dans une infrastructure sécurisée, redondante et administrée par BZ Telecom.
            Les entreprises profitent ainsi des plus récentes technologies tout en éliminant les coûts et la complexité
            liés à la gestion d'un PBX traditionnel.
          </p>
          <p>
            Notre solution accompagne les entreprises dans leur croissance, améliore l'expérience client et offre une
            grande mobilité aux employés. Que les équipes travaillent au bureau, à domicile ou sur la route, elles
            disposent des mêmes fonctionnalités et utilisent leur numéro professionnel partout.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Cas d'usage typiques</h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {hostedTelephonyUseCases.map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Avantages à expliquer au client</h4>
          <div className="space-y-3">
            {hostedTelephonyAdvantages.map((item) => (
              <div key={item.title} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <h5 className="text-sm font-bold text-slate-900">{item.title}</h5>
                <p className="mt-1 text-sm text-slate-700 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-slate-900 text-white p-3 rounded-xl">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-xl font-bold text-gray-900">Wiki téléphonie pour les ventes</h4>
            <p className="text-sm text-gray-700 leading-relaxed mt-1">
              Définitions simples pour expliquer les termes techniques sans perdre le client dans le jargon.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {telephonyGlossary.map((item) => (
            <div key={item.term} className="bg-white rounded-lg border border-slate-200 p-4">
              <h5 className="text-sm font-bold text-slate-900">{item.term}</h5>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{item.plain}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {bzTelecomServiceProducts.map((group) => (
          <section key={group.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h4 className="text-lg font-bold text-gray-900 mb-3">{group.title}</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {group.items.map((item) => (
                <li key={item} className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <section className="rounded-xl border border-purple-200 bg-purple-50/70 p-5">
          <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-700" />
            Téléphonie intelligente avec IA
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {bzTelecomAiFeatures.map((feature) => (
              <li key={feature} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-700" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-cyan-200 bg-cyan-50/70 p-5">
          <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-cyan-700" />
            Adaptée à votre façon de travailler
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {bzTelecomIdealFor.map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-800" />
            Infrastructure fiable et sécurisée
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            {bzTelecomInfrastructure.map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-800" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="rounded-xl border border-blue-200 bg-blue-50/70 p-5">
        <h4 className="text-lg font-bold text-gray-900">Pourquoi choisir BZ Telecom?</h4>
        <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
          {bzTelecomWhyParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 text-sm text-gray-700">
          {bzTelecomReasons.map((reason) => (
            <li key={reason} className="flex gap-2 rounded-lg border border-blue-100 bg-white p-3">
              <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-slate-900 text-white p-3 rounded-xl">
            <PackageCheck className="w-6 h-6" />
          </div>
          <div>
            <h4 className="text-xl font-bold text-gray-900">Produits rattachés</h4>
            <p className="text-sm text-gray-700 leading-relaxed mt-1">
              Définitions professionnelles utilisables dans une brochure, sur le site Web ou dans une présentation de BZ Telecom.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {bzTelecomRelatedProducts.map((product) => (
            <article key={product.title} className="rounded-xl border border-slate-200 bg-white p-5">
              <h5 className="text-lg font-bold text-gray-900">{product.title}</h5>
              <div className="mt-3 space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>{product.description}</p>
                <p>{product.note}</p>
                {product.audience && <p>{product.audience}</p>}
              </div>

              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                {product.features.map((feature) => (
                  <li key={feature} className="flex gap-2">
                    <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-700" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {product.details?.map((detail) => (
                <div key={detail.title} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <h6 className="text-sm font-bold text-slate-900">{detail.title}</h6>
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    {detail.items.map((item) => (
                      <li key={item} className="flex gap-2">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-700" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

// Psychologie & Impact
function PsychologieTab() {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">L'Impact Psychologique</h3>
        <p className="text-gray-700 leading-relaxed">
          Un document bien structuré, professionnel et normé crée un <strong>impact psychologique</strong> 
          puissant sur le client. Il renforce la <strong>confiance</strong>, la <strong>crédibilité</strong> 
          et la <strong>perception de valeur</strong>.
        </p>
      </div>

      {/* Graphique d'impact */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-600" />
          Impact Psychologique Mesuré
        </h4>
        <div className="space-y-4">
          {[
            { metric: 'Confiance Client', before: 60, after: 92, color: 'bg-blue-500' },
            { metric: 'Perception de Valeur', before: 55, after: 88, color: 'bg-emerald-500' },
            { metric: 'Différenciation', before: 45, after: 85, color: 'bg-purple-500' },
            { metric: 'Satisfaction', before: 70, after: 95, color: 'bg-amber-500' },
          ].map((item, idx) => (
            <div key={idx}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">{item.metric}</span>
                <span className="text-sm font-bold text-gray-900">{item.after}%</span>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div 
                    className="bg-gray-400 h-6 rounded-full"
                    style={{ width: `${item.before}%` }}
                  />
                  <div className="absolute top-0 left-0 text-xs text-gray-600 px-2 py-1">
                    Avant: {item.before}%
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div 
                    className={`${item.color} h-6 rounded-full`}
                    style={{ width: `${item.after}%` }}
                  />
                  <div className="absolute top-0 left-0 text-xs text-white px-2 py-1 font-semibold">
                    Après: {item.after}%
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">✅ Confiance</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            Un document normé ITIL/COBIT/NIST démontre notre <strong>professionnalisme</strong> 
            et notre <strong>maîtrise</strong> des standards internationaux.
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">💎 Valeur Perçue</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            La qualité visuelle et structurelle du document renforce la <strong>perception de valeur</strong> 
            et justifie l'investissement client.
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">🚀 Différenciation</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            Des documents de cette qualité nous <strong>différencient</strong> de la concurrence 
            et positionnent BZ comme leader en excellence TI.
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h4 className="text-lg font-bold text-gray-900 mb-3">📈 Évolution</h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            Chaque document aide le client à <strong>évoluer</strong>, à <strong>progresser</strong> 
            et à atteindre ses objectifs stratégiques.
          </p>
        </div>
      </div>
    </div>
  );
}

// Processus Complet
function ProcessusTab() {
  return (
    <div className="space-y-6">
      {/* Flowchart Méthodologie */}
      <div>
        <MethodologyFlowchart />
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Workflow className="w-6 h-6 text-indigo-600" />
          Le Processus Détaillé - Workflow Complet
        </h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          Chaque document suit un processus structuré qui garantit qualité, cohérence et valeur. 
          <strong className="text-indigo-700"> 80% des livrables client</strong> passent par ce processus :
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { 
              step: 1, 
              title: 'Création & Normalisation', 
              desc: 'Dany et les gestionnaires créent le type de document et son template de formulaire holistique, normé selon ITIL/COBIT/NIST/ISO. Définition des standards et structure.', 
              color: 'from-blue-500 to-indigo-600',
              standards: 'ITIL 4, COBIT 2019, NIST CSF 2.0, ISO 27001'
            },
            { 
              step: 2, 
              title: 'Prise de Besoin Holistique', 
              desc: 'Complétion rigoureuse du formulaire holistique associé au type de document. Capture complète des besoins client, contexte, contraintes et objectifs.', 
              color: 'from-emerald-500 to-teal-600',
              standards: 'Questionnaire structuré multi-phases'
            },
            { 
              step: 3, 
              title: 'Génération IA (GPT-5.2)', 
              desc: 'Sélection du client, du template de formulaire et du niveau (1-5). L\'IA génère le document normé selon les standards, avec graphiques professionnels intégrés.', 
              color: 'from-purple-500 to-pink-600',
              standards: 'Génération automatique alignée standards'
            },
            { 
              step: 4, 
              title: 'Expertise Humaine (💎 Richesse BZ)', 
              desc: 'L\'expert humain analyse, ajuste et perfectionne le document selon son jugement professionnel. Validation, personnalisation et enrichissement contextuel.', 
              color: 'from-amber-500 to-orange-600',
              standards: 'Validation experte, personnalisation'
            },
            { 
              step: 5, 
              title: 'Approbation & Qualité', 
              desc: 'Validation par Dany (approval_dany) et spécialiste (approval_specialist). Système de notation 1-5 étoiles garantissant la qualité avant livraison.', 
              color: 'from-red-500 to-rose-600',
              standards: 'Système qualité 1-5 étoiles'
            },
            { 
              step: 6, 
              title: 'Livraison Client PRIsME', 
              desc: 'Le document approuvé est livré dans l\'espace client PRIsME, visible selon les permissions. Création de valeur et renforcement de la relation client.', 
              color: 'from-green-500 to-emerald-600',
              standards: 'Livraison sécurisée, traçable'
            },
          ].map((item) => (
            <div key={item.step} className={`bg-gradient-to-br ${item.color} text-white p-5 rounded-lg shadow-lg border-2 border-white/20`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm font-bold flex items-center justify-center flex-shrink-0 text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-white mb-2 text-lg">{item.title}</h4>
                  <p className="text-sm text-white/90 leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 mt-2">
                    <p className="text-xs text-white/80 font-semibold">{item.standards}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section Temps de Génération */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border-2 border-blue-300">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-blue-600" />
          Temps de Génération - Efficacité Maximale
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { phase: 'Génération IA', temps: '5 min', color: 'bg-purple-500', desc: 'Document brut généré' },
            { phase: 'Validation Expert', temps: '15 min', color: 'bg-amber-500', desc: 'Révision et ajustements' },
            { phase: 'Enrichissement', temps: '20 min', color: 'bg-blue-500', desc: 'Personnalisation finale' },
            { phase: 'Total', temps: '40 min', color: 'bg-emerald-500', desc: 'vs plusieurs heures manuellement' },
          ].map((item) => (
            <div key={item.phase} className="bg-white rounded-lg p-4 border-2 border-blue-200 text-center">
              <div className={`${item.color} text-white rounded-lg p-3 mb-2`}>
                <div className="text-2xl font-bold">{item.temps}</div>
                <div className="text-xs opacity-90">{item.phase}</div>
              </div>
              <p className="text-xs text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Section Types de Documents */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border-2 border-emerald-300">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-6 h-6 text-emerald-600" />
          Catalogue de Documents PRIsME
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          PRIsME génère <strong>300-400 types de documents</strong> répartis en plusieurs catégories :
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { category: 'Stratégiques', count: '45+', docs: 'Plan Directeur TI, Roadmap, Budget', color: 'bg-blue-500' },
            { category: 'Cybersécurité', count: '38+', docs: 'Politiques, Plans, Rapports incidents', color: 'bg-red-500' },
            { category: 'Gouvernance', count: '33+', docs: 'RACI, Comités, Processus', color: 'bg-purple-500' },
            { category: 'Opérationnels', count: '24+', docs: 'Procédures, Guides, Checklists', color: 'bg-indigo-500' },
            { category: 'Conformité', count: '22+', docs: 'Audits, Conformité Loi 25', color: 'bg-green-500' },
            { category: 'Ventes', count: '33+', docs: 'ODS, Propositions, Contrats', color: 'bg-emerald-500' },
          ].map((item) => (
            <div key={item.category} className="bg-white rounded-lg p-4 border-2 border-emerald-200">
              <div className={`${item.color} text-white rounded-lg p-3 mb-2 text-center`}>
                <div className="text-xl font-bold">{item.count}</div>
                <div className="text-sm">{item.category}</div>
              </div>
              <p className="text-xs text-gray-600">{item.docs}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-xl">
        <h4 className="text-xl font-bold text-green-900 mb-2 flex items-center gap-2">
          <Rocket className="w-5 h-5" />
          Résultat Final
        </h4>
        <p className="text-green-800 leading-relaxed">
          Un document <strong>normé</strong>, <strong>professionnel</strong> et <strong>de valeur</strong> 
          qui renforce la crédibilité BZ et aide le client à évoluer. Chaque document contribue à la 
          richesse documentaire PRIsME et à la différenciation concurrentielle. 
          <strong className="text-green-700"> 80% des livrables client</strong> sont générés par ce processus, 
          faisant de la gestion documentaire le <strong>cœur de la valeur ajoutée BZ</strong>.
        </p>
      </div>
    </div>
  );
}
